import { createContext, useState } from "react";
import type { FC } from "react";
import Progress from "./progress";
import { confirmAlert } from "react-confirm-alert";
import "../../public/css/react-confirm-alert.css";
import { MESSAGES } from "../constants/const";

/** ダイアログ表示待機中にこのエラーが発生した場合はメッセージとして表示する */
export class ShownError extends Error {}

export type MessageDialogOptions = {
  message?: string | string[];
  type?: "info" | "confirm" | "success" | "warning" | "error" | "orderCompletion";
  confirm?: boolean;
  onClickPositiveButton?: Function;
  onClickNegativeButton?: Function;
  showPositiveButton?: boolean;
  showNegativeButton?: boolean;
  showOnResolved?:
    | Promise<MessageDialogOptions | void>
    | (() => Promise<MessageDialogOptions | void>);
  window?: Window;
};
export const showMessage = async (options: MessageDialogOptions) => {
  const { showOnResolved } = options;

  let resolvedOptions;

  // 非同期処理終了まで待機 Promiseがメッセージを返した場合は表示するメッセージをそのメッセージに差し替える
  if (showOnResolved) {
    try {
      if (showOnResolved instanceof Promise) {
        resolvedOptions = await showOnResolved;
      }

      if (showOnResolved instanceof Function) {
        resolvedOptions = await showOnResolved();
      }
    } catch (e) {
      console.error(e);

      resolvedOptions = {
        message: e instanceof ShownError ? e.message : MESSAGES.ERROR_MESSAGE_DIALOG_UNHANDLED,
        type: "error",
      };
    }
  }

  console.log("receive resolvedOptions", resolvedOptions);

  const mergedOptions = {
    message: "",
    type: "info",
    ...options,
    ...resolvedOptions,
  };

  const {
    type,
    message,
    onClickPositiveButton,
    onClickNegativeButton,
    window: win,
  } = mergedOptions;

  const confirm = type === "confirm" || type === "orderCompletion" || mergedOptions.confirm;

  const messages = [message].flatMap(message => message);

  const rootClassNames = ["custom-ui", type].join(" ").trim();

  const title = {
    info: "メッセージ",
    confirm: "確認",
    success: "成功",
    warning: "注意",
    error: "エラー",
    orderCompletion: "完了",
  }[type];

  let positiveButtonLabel = "";
  let negativeButtonLabel = "No";
  if (type === "confirm") {
    positiveButtonLabel = "Yes";
  } else if (type === "orderCompletion") {
    positiveButtonLabel = "発注登録一覧へ";
    negativeButtonLabel = "このウィンドウを閉じる";
  } else {
    positiveButtonLabel = "OK";
  }

  const alertMessage =
    type === "orderCompletion" ? "ホーム画面を再読み込みすることで反映してください" : "";

  const positiveClassNames = [
    "positive",
    mergedOptions.showPositiveButton === false ? "d-none" : "",
  ]
    .join(" ")
    .trim();
  const negativeClassNames = [
    "negative",
    mergedOptions.showNegativeButton === false ? "d-none" : confirm ? "" : "d-none",
  ]
    .join(" ")
    .trim();

  // https://pictogrammers.com/library/mdi/
  const mdiAlertCircleOutline =
    "M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z";
  const mdiHelpCircleOutline =
    "M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z";
  const mdiCheckCircleOutline =
    "M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z";
  const mdiAlertOutline = "M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16";

  const iconSvgPath = {
    info: mdiAlertCircleOutline,
    confirm: mdiHelpCircleOutline,
    success: mdiCheckCircleOutline,
    warning: mdiAlertOutline,
    error: mdiAlertCircleOutline,
    orderCompletion: mdiHelpCircleOutline,
  }[type];

  return new Promise<boolean>(resolve => {
    return confirmAlert({
      closeOnEscape: false,
      onClickOutside: () => {
        resolve(false);
      },
      customUI: ({ onClose }: any) => {
        return (
          <div className={rootClassNames}>
            <h1>
              <span style={{ position: "relative" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 24 24"
                  style={{ position: "absolute", top: "0.25em", left: "-1.25em" }}
                >
                  <path fill="white" d={iconSvgPath} />
                </svg>
                {title}
              </span>
            </h1>
            {messages.length > 1 ? (
              <ul className="mx-auto text-center" style={{ width: "fit-content" }}>
                {messages.map(message => (
                  <li className="text-left">{message}</li>
                ))}
              </ul>
            ) : (
              <p>{messages.pop()}</p>
            )}
            <button
              className={negativeClassNames}
              onClick={() => {
                onClose();
                if (onClickNegativeButton) {
                  onClickNegativeButton();
                }
                resolve(false);
              }}
            >
              {negativeButtonLabel}
            </button>
            <button
              className={positiveClassNames}
              onClick={() => {
                onClose();
                if (onClickPositiveButton) {
                  onClickPositiveButton();
                }
                resolve(true);
              }}
            >
              {positiveButtonLabel}
            </button>
            {alertMessage !== "" && <div>{alertMessage}</div>}
          </div>
        );
      },
    });
  });
};

export const showInfoMessage = (message: string | string[], options?: MessageDialogOptions) => {
  return showMessage({
    ...options,
    message,
    type: "info",
  });
};

export const showConfirmMessage = (message: string | string[], options?: MessageDialogOptions) => {
  return showMessage({
    ...options,
    message,
    type: "confirm",
  });
};

export const showSuccessMessage = (message: string | string[], options?: MessageDialogOptions) => {
  return showMessage({
    ...options,
    message,
    type: "success",
  });
};

export const showWarningMessage = (
  message: string | string[],
  confirm?: boolean,
  options?: MessageDialogOptions
) => {
  return showMessage({
    ...options,
    message,
    confirm: typeof confirm === "undefined" ? true : confirm,
    type: "warning",
  });
};

export const showErrorMessage = (message: string | string[], options?: MessageDialogOptions) => {
  return showMessage({
    ...options,
    message,
    type: "error",
  });
};

export const showOrderCompletionMessage = (
  message: string | string[],
  options?: MessageDialogOptions
) => {
  return showMessage({
    ...options,
    message,
    type: "orderCompletion",
  });
};

export const showNumberInput = (pageMax: number) => {
  return new Promise<number>(resolve => {
    const title: string = "ファイルアップロード";
    const mdiAlertCircleOutline: string =
      "M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z";
    const rootClassNames = ["custom-ui", "input"].join(" ").trim();
    return confirmAlert({
      customUI: ({ onClose }: any) => {
        return (
          <div className={rootClassNames}>
            <h1>
              <span style={{ position: "relative" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 24 24"
                  style={{ position: "absolute", top: "0.25em", left: "-1.25em" }}
                >
                  <path fill="white" d={mdiAlertCircleOutline} />
                </svg>
                {title}
              </span>
            </h1>
            <div>何ページ目をアップロードしますか？</div>
            {Array.from({
              length: pageMax,
            }).map((_, colIndex) => (
              <button
                key={colIndex}
                className="pageSelect"
                onClick={() => {
                  onClose();
                  resolve(colIndex + 1);
                }}
              >
                {colIndex + 1}
              </button>
            ))}
          </div>
        );
      },
    });
  });
};

interface ConfirmFormProps {
  title: string;
  message: string | string[];
  onClickNegativeButton?: Function;
  onClickPositiveButton?: Function;
}

export const ConfirmForm = ({
  title,
  message,
  onClickNegativeButton,
  onClickPositiveButton,
}: ConfirmFormProps) => {
  // mdiHelpCircleOutline
  const iconSvgPath =
    "M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z";

  const messages = [message].flatMap(message => message);
  const rootClassNames = ["custom-ui", "confirm"].join(" ").trim();

  return (
    <div className={rootClassNames}>
      <h1>
        <span style={{ position: "relative" }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            style={{ position: "absolute", top: "0.25em", left: "-1.25em" }}
          >
            <path fill="white" d={iconSvgPath} />
          </svg>
          {title}
        </span>
      </h1>
      {messages.length > 1 ? (
        <ul className="mx-auto text-center" style={{ width: "fit-content" }}>
          {messages.map(message => (
            <li className="text-left">{message}</li>
          ))}
        </ul>
      ) : (
        <p>{messages.pop()}</p>
      )}
      <button
        className="negative"
        onClick={() => {
          if (onClickNegativeButton) {
            onClickNegativeButton();
          }
        }}
      >
        Cancel
      </button>
      <button
        className="positive"
        onClick={() => {
          if (onClickPositiveButton) {
            onClickPositiveButton();
          }
        }}
      >
        OK
      </button>
    </div>
  );
};

interface TextInputFormProps {
  title: string;
  message: string;
  placeholder?: string;
  prevText?: string;
  onClickNegativeButton?: Function;
  onClickPositiveButton?: Function;
}

export const TextInputForm = ({
  title,
  message,
  placeholder,
  prevText,
  onClickPositiveButton,
  onClickNegativeButton,
}: TextInputFormProps) => {
  const [text, setText] = useState(prevText);
  const negativeClassNames = ["negative"].join(" ").trim();
  const mdiEditIcon: string =
    "M18.13 12L19.39 10.74C19.83 10.3 20.39 10.06 21 10V9L15 3H5C3.89 3 3 3.89 3 5V19C3 20.1 3.89 21 5 21H11V19.13L11.13 19H5V5H12V12H18.13M14 4.5L19.5 10H14V4.5M19.13 13.83L21.17 15.87L15.04 22H13V19.96L19.13 13.83M22.85 14.19L21.87 15.17L19.83 13.13L20.81 12.15C21 11.95 21.33 11.95 21.53 12.15L22.85 13.47C23.05 13.67 23.05 14 22.85 14.19Z";

  const rootClassNames = ["custom-ui", "input"].join(" ").trim();
  return (
    <div className={rootClassNames}>
      <h1>
        <span style={{ position: "relative" }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            style={{ position: "absolute", top: "0.25em", left: "-1.25em" }}
          >
            <path fill="white" d={mdiEditIcon} />
          </svg>
          {title}
        </span>
      </h1>
      <p>{message}</p>
      <textarea
        defaultValue={prevText}
        placeholder={placeholder}
        style={{ resize: "none", width: "80%" }}
        onChange={e => {
          setText(e.target.value);
        }}
      />
      <button
        className={negativeClassNames}
        onClick={() => {
          onClickNegativeButton?.();
        }}
      >
        Cancel
      </button>
      <button
        className="positive"
        onClick={() => {
          onClickPositiveButton?.(text);
        }}
      >
        OK
      </button>
    </div>
  );
};

interface LoadingStateContext {
  isLoading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LoadingContext = createContext<LoadingStateContext>({
  isLoading: false,
  setLoading: () => {},
});

interface AppPaneProps {
  children: any;
}

const AppPane: FC<AppPaneProps> = ({ children }) => {
  const [isLoading, setLoading] = useState(false);

  const loadingTasks: boolean[] = [];

  // 子にローディング状態のアクセサを公開する
  const loadingContext = {
    isLoading,
    setLoading: (value: boolean) => {
      // 並列処理でローディング表示がセットされた場合、それぞれの処理でのローディング非表示がすべて行われるまで実際には非表示にしない
      value ? loadingTasks.push(true) : loadingTasks.pop();
      setLoading(!!loadingTasks.length);
    },
  };

  return (
    <LoadingContext.Provider value={loadingContext}>
      <link href="../../public/css/react-confirm-alert.css" rel="stylesheet" />
      <Progress value={isLoading} subWindow />
      {children}
    </LoadingContext.Provider>
  );
};

export default AppPane;
